/**
 * KonvaInteractionLayerContainer - Container Component
 *
 * Container component responsible for:
 * - Zustand store connection (useStore, useStorePrimitive)
 * - Reading selectedMarkerId, partyRoster, and npcs from the store
 * - Delegating presentation to the pure KonvaInteractionLayer component
 *
 * Note: useGrid(), useGridCalculator(), useGridService() are context/computation
 * hooks (not Zustand store hooks) and remain in the presenter.
 *
 * This follows the Container/Presenter pattern as defined in ADR-034.
 * @see docs/adr/0034-container-presenter-component-pattern.md
 */

import React from 'react';
import { useStore, useStorePrimitive } from '../../hooks/useStore';
import type { PartyMember, NPC, RenderedImageRect, InteractionState } from '../../types/types';
import type { Stage } from 'konva/lib/Stage';
import KonvaInteractionLayer from './KonvaInteractionLayer';

interface KonvaInteractionLayerContainerProps {
  imageRect: RenderedImageRect | null;
  interaction: InteractionState | null;
  drawingMode: string | null;
  hoveredCell: { row: number; col: number } | null;
  tooltipState: { x: number; y: number; visible: boolean; cell: { row: number; col: number } | null };
  gridOrigin: { x: number; y: number };
  saveRegion: () => void;
  stageRef: React.RefObject<Stage | null>;
}

const emptyParty: PartyMember[] = [];
const emptyNpcs: NPC[] = [];

const KonvaInteractionLayerContainer: React.FC<KonvaInteractionLayerContainerProps> = (props) => {
  const selectedMarkerId = useStorePrimitive(state => state.session?.map?.selectedMarkerId);
  const partyRoster = useStore(state => state.session?.roster?.partyRoster) || emptyParty;
  const npcs = useStore(state => state.session?.roster?.npcs) || emptyNpcs;

  return (
    <KonvaInteractionLayer
      {...props}
      selectedMarkerId={selectedMarkerId}
      partyRoster={partyRoster}
      npcs={npcs}
    />
  );
};

export default KonvaInteractionLayerContainer;
